import { FC, useMemo } from 'react';

import { getBadgeCountValue } from 'utils/chatik/getBadgeCountValue';
import { HELP_MAIL_LINK, HELP_PAGE_URL } from 'utils/constants/app';

import { ElementShownAnchor } from '@hh.ru/analytics-js';
import helpModalButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/common/help_modal_button_click';
import helpModalElementShown from '@hh.ru/analytics-js-events/build/clickme/employer/common/help_modal_element_shown';
import { BottomSheet, Card, Modal, Text, VSpacing } from '@hh.ru/magritte-ui';
import {
    ArrowRightOutlinedSize16,
    BubbleRoundDoubleOutlinedSize24,
    CrossOutlinedSize24,
    QuestionCircleOutlinedSize24,
} from '@hh.ru/magritte-ui/icon';
import { MailSolidSize24 } from '@hh.ru/magritte-ui/service';

import useTrls from 'hooks/translation/useTrls';
import useSupportChatik from 'utils/chatik/hooks/useSupportChatik';

import { MagritteModalHeader } from 'components/common/MagritteModalHeader/MagritteModalHeader';
import { HelpModalButton } from 'components/HelpModal/HelpModalButton/HelpModalButton';
import { HelpModalContactPhone } from 'components/HelpModal/HelpModalContactPhone/HelpModalContactPhone';

import { selectIsHelpModalVisible, setIsHelpModalVisible } from 'store/autoReducers';
import { useDispatch, useSelector } from 'store/utils';

import styles from 'components/HelpModal/HelpModal.module.less';

const TrlKeys = {
    title: 'HelpModal.title',
    description: 'HelpModal.description',
    employerId: 'HelpModal.employerId',
    buttons: {
        knowledgeBase: 'HelpModal.buttons.knowledgeBase',
        mail: 'HelpModal.buttons.mail',
        supportChat: 'HelpModal.buttons.supportChat',
        contact: 'HelpModal.buttons.supportChat.contact',
    },
    contacts: {
        mskAndRegion: {
            label: 'HelpModal.helpPhones.mskAndRegion.label',
            phone: 'HelpModal.helpPhones.mskAndRegion.phone',
        },
        spbAndRegion: {
            label: 'HelpModal.helpPhones.spbAndRegion.label',
            phone: 'HelpModal.helpPhones.spbAndRegion.phone',
        },
        otherRegions: {
            label: 'HelpModal.helpPhones.otherRegions.label',
            phone: 'HelpModal.helpPhones.otherRegions.phone',
        },
    },
};

export const HelpModal: FC = () => {
    const employerId = useSelector((state) => state.user?.user?.employerId);
    const unreadSupportCount = useSelector((state) => state.chatikUnreadSupportCount);
    const isVisible = useSelector(selectIsHelpModalVisible);

    const trls = useTrls();
    const dispatch = useDispatch();
    const badgeValue = useMemo(() => getBadgeCountValue(unreadSupportCount), [unreadSupportCount]);

    const closeModal = () => {
        helpModalButtonClick({ buttonName: 'help_modal_close' });
        dispatch(setIsHelpModalVisible(false));
    };

    const { handleChatikClick } = useSupportChatik(closeModal);

    const content = (
        <ElementShownAnchor fn={helpModalElementShown}>
            {employerId && (
                <Card padding={16} borderRadius={24} style="secondary" stretched>
                    <div className={styles.employerIdCard}>
                        <Text typography="label-2-regular" style="secondary">
                            {trls(TrlKeys.employerId)}
                        </Text>
                        <ArrowRightOutlinedSize16 initialColor="secondary" />
                        <Text typography="label-2-regular" style="primary">
                            {employerId}
                        </Text>
                    </div>
                </Card>
            )}
            <VSpacing default={24} />
            <div className={styles.helpButtons}>
                <HelpModalButton
                    icon={
                        <QuestionCircleOutlinedSize24
                            padding={12}
                            borderRadius={12}
                            initialColor="primary"
                            backgroundStyle="secondary"
                        />
                    }
                    title={trls(TrlKeys.buttons.knowledgeBase)}
                    href={HELP_PAGE_URL}
                    onClick={() => helpModalButtonClick({ buttonName: 'help_modal_knowledge_base' })}
                />
                <HelpModalButton
                    icon={
                        <MailSolidSize24
                            padding={12}
                            borderRadius={12}
                            initialColor="primary"
                            backgroundStyle="secondary"
                        />
                    }
                    title={trls(TrlKeys.buttons.mail)}
                    href={HELP_MAIL_LINK}
                    onClick={() => helpModalButtonClick({ buttonName: 'help_modal_mail' })}
                />
            </div>
            <VSpacing default={12} />
            <HelpModalButton
                icon={
                    <BubbleRoundDoubleOutlinedSize24
                        padding={12}
                        borderRadius={12}
                        initialColor="primary"
                        backgroundStyle="secondary"
                    />
                }
                title={trls(TrlKeys.buttons.supportChat)}
                onClick={() => {
                    handleChatikClick();
                    helpModalButtonClick({ buttonName: 'help_modal_support_chat' });
                }}
                badgeValue={badgeValue}
                buttonLabel={trls(TrlKeys.buttons.contact)}
            />
        </ElementShownAnchor>
    );

    const footer = (
        <div className={styles.helpFooter}>
            {Object.values(TrlKeys.contacts).map((region) => (
                <HelpModalContactPhone label={trls(region.label)} phone={trls(region.phone)} key={region.label} />
            ))}
        </div>
    );

    return (
        <>
            <Modal
                size="medium"
                visible={isVisible}
                onClose={closeModal}
                header={
                    <MagritteModalHeader
                        title={trls(TrlKeys.title)}
                        subtitle={
                            <div className={styles.modalHeaderSubtitle}>
                                <Text style="secondary" typography="label-2-regular">
                                    {trls(TrlKeys.description)}
                                </Text>
                            </div>
                        }
                        actions={<CrossOutlinedSize24 initialColor="secondary" onClick={closeModal} />}
                        centered
                    />
                }
                data-qa="help-modal"
                footer={footer}
            >
                {content}
                <VSpacing default={32} />
            </Modal>
            <BottomSheet
                height="content"
                visible={isVisible}
                onClose={closeModal}
                footer={footer}
                data-qa="help-modal-bottom-sheet"
            >
                <div className={styles.bottomSheetHeaderClose}>
                    <CrossOutlinedSize24 initialColor="tertiary" onClick={closeModal} />
                </div>
                <div className={styles.bottomSheetHeaderText}>
                    <Text typography="title-5-semibold">{trls(TrlKeys.title)}</Text>
                    <VSpacing default={6} />
                    <Text style="secondary" typography="paragraph-2-regular">
                        {trls(TrlKeys.description)}
                    </Text>
                </div>
                <VSpacing default={24} />
                {content}
                <VSpacing default={16} />
            </BottomSheet>
        </>
    );
};
